import {
  Armchair,
  Boxes,
  Building2,
  Camera,
  Clock,
  DollarSign,
  Home,
  Leaf,
  MapPin,
  Truck,
  Warehouse,
  type LucideIcon,
} from "lucide-react";

export type Benefit = { title: string; desc: string };

export type ServiceBlock =
  | { kind: "callout"; heading: string; copy?: string; cta?: { label: string; href: string } }
  | { kind: "statement"; lines: string[]; copy?: string }
  | { kind: "benefits"; heading: string; items: Benefit[] }
  | { kind: "list"; heading: string; items: string[] }
  | { kind: "crossover" }
  | { kind: "beforeAfter" };

export type WhyKey =
  | "lifting"
  | "upfront"
  | "fast"
  | "photos"
  | "melbourne"
  | "loads"
  | "scheduled";

export const whyOptions: Record<WhyKey, { icon: LucideIcon; title: string; desc: string }> = {
  lifting: {
    icon: Truck,
    title: "We Do the Heavy Lifting",
    desc: "No dragging everything outside or loading a skip yourself.",
  },
  upfront: {
    icon: DollarSign,
    title: "Upfront Quotes",
    desc: "Know the agreed price before the job begins.",
  },
  fast: {
    icon: Clock,
    title: "Fast Pickup Availability",
    desc: "Same-day and short-notice bookings may be available depending on the schedule.",
  },
  photos: {
    icon: Camera,
    title: "Easy Photo Quotes",
    desc: "Send us a few photos so we can assess the job.",
  },
  melbourne: {
    icon: MapPin,
    title: "Melbourne Based",
    desc: "Local rubbish removal across Melbourne.",
  },
  loads: {
    icon: Boxes,
    title: "Single Items to Full Loads",
    desc: "From one bulky item through to multiple loads.",
  },
  scheduled: {
    icon: Building2,
    title: "Scheduled Pickups",
    desc: "Clear communication and a pickup time arranged with you.",
  },
};

export interface WasteService {
  slug: string;
  icon: LucideIcon;
  cardTitle: string;
  cardDesc: string;
  title: string;
  metaDescription: string;
  label: string;
  h1: string;
  heroSub: string;
  removeHeading: string;
  removeIntro: string;
  removeItems: string[];
  why: WhyKey[];
  blocks: ServiceBlock[];
  specialItems?: boolean;
  related?: { label: string; href: string; desc: string }[];
  faqs: { q: string; a: string }[];
}

export const wasteServices: WasteService[] = [
  {
    slug: "household-rubbish-removal",
    icon: Home,
    cardTitle: "Household Rubbish",
    cardDesc: "General household junk, clutter and unwanted belongings.",
    title: "Household Rubbish Removal Melbourne | OZ WASTE CO.",
    metaDescription:
      "Household rubbish removal across Melbourne. We carry it out, load it and remove it. Send a few photos for a free, upfront quote from OZ WASTE CO.",
    label: "Rubbish Removal Across Melbourne",
    h1: "Household Rubbish Removal",
    heroSub:
      "Clear unwanted household rubbish, furniture and clutter without doing the heavy lifting yourself.",
    removeHeading: "Clear the Clutter Without the Hassle",
    removeIntro:
      "Whether it's a few unwanted items or a larger household clean-out, our team can handle the lifting, loading and removal.",
    removeItems: [
      "Household junk",
      "Unwanted belongings",
      "Boxes",
      "Old furniture",
      "Small appliances",
      "Shelving",
      "Outdoor items",
      "General clutter",
      "Miscellaneous non-hazardous household rubbish",
    ],
    why: ["lifting", "upfront", "fast", "photos", "melbourne"],
    blocks: [
      {
        kind: "benefits",
        heading: "Why Use OZ WASTE Instead of a Skip?",
        items: [
          {
            title: "No Loading the Skip Yourself",
            desc: "Leave the items where they are and our team can carry them out.",
          },
          { title: "No Bin Sitting at Your Property", desc: "We arrive, load and leave." },
          {
            title: "From a Few Items to Larger Loads",
            desc: "You don't need to hire an entire skip just to remove a small amount of rubbish.",
          },
        ],
      },
    ],
    specialItems: true,
    related: [
      {
        label: "Furniture Removal",
        href: "/furniture-removal",
        desc: "Couches, mattresses and bulky furniture carried out and removed.",
      },
      {
        label: "Property Clear-Outs",
        href: "/property-clear-outs",
        desc: "Full home, rental and vacant property clear-outs.",
      },
    ],
    faqs: [
      {
        q: "Do I need to put everything outside first?",
        a: "No. Our team can collect items from inside the property where access is safe and reasonable.",
      },
      {
        q: "Can you remove just a few items?",
        a: "Yes. Jobs can range from a few unwanted items through to larger household clear-outs.",
      },
      {
        q: "Can I send photos for a quote?",
        a: "Yes. Photos are one of the easiest ways for us to understand what needs removing.",
      },
      {
        q: "Do you remove furniture as part of household rubbish removal?",
        a: "Yes. Furniture and other bulky household items can be included.",
      },
      {
        q: "What if I'm not sure whether you can take something?",
        a: "Send us a photo or description before booking and we'll let you know.",
      },
    ],
  },
  {
    slug: "furniture-removal",
    icon: Armchair,
    cardTitle: "Furniture Removal",
    cardDesc: "Couches, mattresses, tables, cabinets and bulky furniture.",
    title: "Furniture Removal Melbourne | OZ WASTE CO.",
    metaDescription:
      "Old or unwanted furniture removed across Melbourne. Couches, mattresses, wardrobes and more. We carry it out and load it. Free photo quotes.",
    label: "Furniture Removal Across Melbourne",
    h1: "Furniture Removal",
    heroSub:
      "Need old or unwanted furniture gone? Our team handles the lifting, loading and removal for you.",
    removeHeading: "Heavy Furniture? Leave It to Us.",
    removeIntro:
      "You don't need to carry heavy furniture outside first. Our team can collect it from the property and load it for removal.",
    removeItems: [
      "Couches",
      "Sofas",
      "Mattresses",
      "Bed frames",
      "Dining tables",
      "Chairs",
      "Wardrobes",
      "Cabinets",
      "Shelving",
      "Desks",
      "Outdoor furniture",
      "Bulky household furniture",
    ],
    why: ["lifting", "upfront", "photos", "fast"],
    blocks: [
      {
        kind: "list",
        heading: "Suitable For",
        items: [
          "Replacing old furniture",
          "Moving house",
          "Downsizing",
          "Clearing spare rooms",
          "Disposing of damaged furniture",
          "Preparing a property for sale or lease",
          "General decluttering",
        ],
      },
      {
        kind: "statement",
        lines: ["NO DRAGGING IT", "TO THE KERB."],
        copy: "Leave the heavy lifting to us.",
      },
    ],
    related: [
      {
        label: "Household Rubbish Removal",
        href: "/household-rubbish-removal",
        desc: "General household junk, boxes and clutter cleared out.",
      },
      {
        label: "Property Clear-Outs",
        href: "/property-clear-outs",
        desc: "Clearing a whole home, rental or vacant property.",
      },
    ],
    faqs: [
      { q: "Do you remove couches?", a: "Yes." },
      { q: "Do you remove mattresses?", a: "Yes." },
      {
        q: "Do I need to dismantle furniture first?",
        a: "Not necessarily. Let us know what the item is and we can assess the best way to remove it.",
      },
      {
        q: "Can furniture be collected from upstairs?",
        a: "Potentially, subject to safe access. Let us know about stairs when requesting your quote.",
      },
      {
        q: "Can you remove multiple pieces of furniture at once?",
        a: "Yes. Send photos of everything you need removed so we can assess the job together.",
      },
    ],
  },
  {
    slug: "move-out-clear-outs",
    icon: Boxes,
    cardTitle: "Move-Out Clear-Outs",
    cardDesc: "Everything you're not taking with you when you move.",
    title: "Move-Out Clear-Outs Melbourne | OZ WASTE CO.",
    metaDescription:
      "Moving house in Melbourne? OZ WASTE CO. removes the furniture, rubbish and leftover items you're not taking with you. Free photo quotes.",
    label: "Moving Out Across Melbourne",
    h1: "Move-Out Clear-Outs",
    heroSub: "Moving house? Take what you want and let us remove what you're leaving behind.",
    removeHeading: "What We Can Clear",
    removeIntro:
      "Tell us what's staying behind and we'll handle the lifting, loading and removal before or after your move.",
    removeItems: [
      "Unwanted furniture",
      "Old mattresses",
      "Appliances",
      "Boxes",
      "Household rubbish",
      "Garage items",
      "Outdoor furniture",
      "Leftover belongings",
      "General clutter",
    ],
    why: ["lifting", "upfront", "fast", "photos"],
    blocks: [
      {
        kind: "statement",
        lines: ["KEEPING IT? MOVE IT.", "DON'T WANT IT? CLEAR IT."],
        copy: "OZ WASTE can remove the items you're not taking to the new property.",
      },
      { kind: "crossover" },
    ],
    related: [
      {
        label: "Furniture Removal",
        href: "/furniture-removal",
        desc: "Couches, beds and bulky furniture removed from the property.",
      },
      {
        label: "Property Clear-Outs",
        href: "/property-clear-outs",
        desc: "Clearing an entire home, rental or vacant property.",
      },
    ],
    faqs: [
      { q: "Can you clear rubbish before moving day?", a: "Yes, subject to availability." },
      { q: "Can you clear items after my move is finished?", a: "Yes." },
      {
        q: "Can OZ MOVE handle the belongings I'm keeping?",
        a: "Yes. OZ MOVE CO. handles the moving side, so you can arrange the move and the clear-out separately.",
      },
      {
        q: "Can you remove furniture and appliances?",
        a: "Yes, subject to the type of item and access.",
      },
      { q: "Can I send photos of everything I'm leaving behind?", a: "Yes. This is encouraged." },
    ],
  },
  {
    slug: "garage-shed-clear-outs",
    icon: Warehouse,
    cardTitle: "Garage & Shed Clear-Outs",
    cardDesc: "Old furniture, boxes, equipment and accumulated clutter.",
    title: "Garage & Shed Clear-Outs Melbourne | OZ WASTE CO.",
    metaDescription:
      "Garage and shed clear-outs across Melbourne. Boxes, old furniture, shelving and clutter removed by our team. Send photos for a free quote.",
    label: "Garage & Shed Clear-Outs",
    h1: "Garage & Shed Clear-Outs",
    heroSub:
      "Clear the clutter and get your space back. We handle the lifting, loading and removal.",
    removeHeading: "Get Your Garage Back",
    removeIntro:
      "Years of boxes, furniture and unwanted items can build up quickly. OZ WASTE can help clear the space without you having to load everything yourself.",
    removeItems: [
      "Boxes",
      "Old furniture",
      "Shelving",
      "Household items",
      "Unwanted equipment",
      "Outdoor items",
      "Garden furniture",
      "General clutter",
      "Non-hazardous rubbish",
      "Bulky items",
    ],
    why: ["lifting", "upfront", "photos", "loads"],
    blocks: [
      {
        kind: "list",
        heading: "Suitable For",
        items: [
          "Garage clean-outs",
          "Shed clean-outs",
          "Preparing for a move",
          "Making room for vehicles",
          "Decluttering",
          "Property preparation",
          "Downsizing",
          "Larger household clean-ups",
        ],
      },
      { kind: "beforeAfter" },
    ],
    specialItems: true,
    related: [
      {
        label: "Household Rubbish Removal",
        href: "/household-rubbish-removal",
        desc: "Everyday household junk and clutter removed.",
      },
      {
        label: "Property Clear-Outs",
        href: "/property-clear-outs",
        desc: "Larger clear-outs covering the whole property.",
      },
    ],
    faqs: [
      {
        q: "Do I need to sort everything first?",
        a: "No. Tell us what needs to go and what needs to stay.",
      },
      { q: "Can you remove large furniture from a garage?", a: "Yes." },
      {
        q: "Can you clear the whole garage?",
        a: "Yes. Larger clear-outs can be quoted based on volume and access.",
      },
      {
        q: "Can you remove hazardous chemicals or paint?",
        a: "Certain materials may require special arrangements. Ask before booking.",
      },
      { q: "Can I send photos instead of listing everything?", a: "Yes. Photos are encouraged." },
    ],
  },
  {
    slug: "commercial-rubbish-removal",
    icon: Building2,
    cardTitle: "Office & Commercial",
    cardDesc: "Furniture, equipment and unwanted items from workplaces and commercial spaces.",
    title: "Office & Commercial Rubbish Removal Melbourne | OZ WASTE CO.",
    metaDescription:
      "Office and commercial rubbish removal in Melbourne. Desks, chairs, cabinets and business clutter cleared with upfront quotes and scheduled pickups.",
    label: "Commercial Rubbish Removal",
    h1: "Office & Commercial Rubbish Removal",
    heroSub:
      "Clear unwanted office furniture, equipment and commercial clutter with a professional removal team.",
    removeHeading: "Commercial Items We Remove",
    removeIntro:
      "From a single office fit-out to a full tenancy exit, we quote upfront and arrange a pickup that suits your operation.",
    removeItems: [
      "Office desks",
      "Office chairs",
      "Cabinets",
      "Shelving",
      "Tables",
      "Unwanted furniture",
      "Office equipment",
      "Boxes",
      "Storage items",
      "General commercial rubbish",
      "Non-hazardous business waste",
    ],
    why: ["upfront", "scheduled", "lifting", "loads"],
    blocks: [
      {
        kind: "list",
        heading: "Suitable For",
        items: [
          "Office clear-outs",
          "Relocations",
          "Office refurbishments",
          "Tenancy exits",
          "Warehouse or storage areas",
          "Furniture replacements",
          "Business downsizing",
          "Commercial property preparation",
        ],
      },
      {
        kind: "callout",
        heading: "Clearing a Larger Space?",
        copy: "We can quote larger commercial jobs based on the volume of items, access requirements and number of loads required.",
        cta: { label: "Request a Quote", href: "/#quote" },
      },
      {
        kind: "benefits",
        heading: "How We Work With Businesses",
        items: [
          {
            title: "Clear Communication",
            desc: "One point of contact from the quote through to the pickup.",
          },
          {
            title: "Scheduled Pickup",
            desc: "A pickup window agreed with you, with minimal disruption where reasonably possible.",
          },
          {
            title: "Larger-Load Capability",
            desc: "Multiple loads can be quoted where the volume requires it.",
          },
        ],
      },
    ],
    specialItems: true,
    faqs: [
      { q: "Can you remove office desks and chairs?", a: "Yes." },
      {
        q: "Can you clear an entire office?",
        a: "Yes. Larger jobs can be quoted based on the size and access requirements.",
      },
      {
        q: "Can you work around business hours?",
        a: "Scheduling can be discussed when requesting the quote.",
      },
      {
        q: "Can you remove electronics?",
        a: "Some electrical equipment may be accepted depending on the item and disposal requirements. Ask when requesting your quote.",
      },
      {
        q: "Can we send photos or an inventory?",
        a: "Yes. Photos and item lists can help us assess commercial jobs.",
      },
    ],
  },
  {
    slug: "property-clear-outs",
    icon: Home,
    cardTitle: "Property Clear-Outs",
    cardDesc: "Larger clean-outs for homes, rentals and other properties.",
    title: "Property Clear-Outs Melbourne | OZ WASTE CO.",
    metaDescription:
      "Full property clear-outs across Melbourne. Homes, rentals, vacant and end-of-lease properties cleared of furniture, rubbish and bulky items.",
    label: "Property Clear-Outs Across Melbourne",
    h1: "Property Clear-Outs",
    heroSub:
      "Need a whole property cleared? Our team can remove unwanted furniture, household rubbish and bulky items from homes, rentals and other properties.",
    removeHeading: "What We Can Clear",
    removeIntro:
      "Tell us what's staying and what's going. Our team handles the carrying, loading and removal.",
    removeItems: [
      "Unwanted furniture",
      "Mattresses",
      "Appliances",
      "Household rubbish",
      "Boxes",
      "General clutter",
      "Garage contents",
      "Shed contents",
      "Outdoor furniture",
      "Leftover belongings",
      "Bulky household items",
      "General non-hazardous waste",
    ],
    why: ["lifting", "upfront", "photos", "loads", "melbourne"],
    blocks: [
      {
        kind: "list",
        heading: "Suitable For",
        items: [
          "Full home clear-outs",
          "Rental property clear-outs",
          "End-of-lease clear-outs",
          "Properties being prepared for sale",
          "Inherited properties",
          "Deceased estate clear-outs",
          "Downsizing",
          "Large household clean-outs",
          "Vacant properties",
        ],
      },
      {
        kind: "callout",
        heading: "From a Few Rooms to the Whole Property",
        copy: "For larger clear-outs, send us photos or details of the property and we'll assess the volume, access and work required.",
      },
      {
        kind: "callout",
        heading: "Clearing a Larger Property?",
        copy: "No problem. Larger jobs can be quoted based on the amount being removed, access requirements and number of loads required.",
        cta: { label: "Send Us Photos", href: "/#quote" },
      },
      {
        kind: "benefits",
        heading: "Property Managers & Landlords",
        items: [
          {
            title: "Rental and Vacant Properties",
            desc: "Need unwanted items cleared from a rental or vacant property? Send us the details and we can assess the job.",
          },
        ],
      },
    ],
    specialItems: true,
    related: [
      {
        label: "Garage & Shed Clear-Outs",
        href: "/garage-shed-clear-outs",
        desc: "Clearing the garage, shed and outdoor storage areas.",
      },
      {
        label: "Furniture Removal",
        href: "/furniture-removal",
        desc: "Bulky furniture and mattresses removed from the property.",
      },
    ],
    faqs: [
      {
        q: "Can you clear an entire house?",
        a: "Yes. Larger clear-outs can be quoted based on the amount of material and access.",
      },
      { q: "Do you clear rental properties?", a: "Yes." },
      {
        q: "Can you remove furniture, mattresses and appliances together?",
        a: "Yes, subject to the type of items being removed.",
      },
      {
        q: "Can you assist with deceased estate clear-outs?",
        a: "Yes. We understand these clear-outs can be difficult, so give our team a call to discuss the property and we'll work to your timeframe.",
      },
      {
        q: "How do I get a quote for a full property?",
        a: "Send photos, details about the property and information about access.",
      },
      {
        q: "What if it takes more than one load?",
        a: "Larger jobs can be quoted based on the number of loads and work required.",
      },
    ],
  },
  {
    slug: "green-waste-removal",
    icon: Leaf,
    cardTitle: "Green Waste Removal",
    cardDesc: "Garden waste, branches, clippings and yard clean-ups.",
    title: "Green Waste Removal Melbourne | OZ WASTE CO.",
    metaDescription:
      "Green waste removal across Melbourne. Branches, hedge clippings, lawn clippings, leaves and garden debris loaded and removed. Free photo quotes from OZ WASTE CO.",
    label: "Green Waste Removal Across Melbourne",
    h1: "Green Waste Removal",
    heroSub:
      "Garden waste piling up? Our team loads and removes branches, clippings and yard debris so you don't have to.",
    removeHeading: "Garden Waste We Remove",
    removeIntro:
      "From a weekend pruning session to a full yard clean-up, tell us what needs to go and we'll handle the loading and removal.",
    removeItems: [
      "Branches and prunings",
      "Hedge trimmings",
      "Lawn clippings",
      "Leaves and garden debris",
      "Small tree cuttings",
      "Plants and shrubs",
      "Garden clean-up waste",
      "Old mulch and bark",
      "Untreated timber offcuts",
      "General green waste",
    ],
    why: ["lifting", "upfront", "fast", "photos", "melbourne"],
    blocks: [
      {
        kind: "list",
        heading: "Suitable For",
        items: [
          "Garden clean-ups",
          "After landscaping or tree work",
          "Seasonal yard tidy-ups",
          "Preparing a property for sale or lease",
          "Clearing overgrown sections",
          "Storm debris clean-ups",
          "End-of-lease garden tidy-ups",
          "Regular property maintenance clear-outs",
        ],
      },
      {
        kind: "benefits",
        heading: "Why Use OZ WASTE for Green Waste?",
        items: [
          {
            title: "No Council-Size Limits",
            desc: "Council green bins fill fast after a big garden clean-up. We can take larger volumes in one visit.",
          },
          {
            title: "No Skip on the Nature Strip",
            desc: "We arrive, load and take it away, no bin sitting out the front for a week.",
          },
          {
            title: "We Do the Loading",
            desc: "Leave the pile where it is where access allows, and our team will load it.",
          },
        ],
      },
      {
        kind: "statement",
        lines: ["YOU PRUNE IT.", "WE REMOVE IT."],
        copy: "Point at the pile and we'll take it from there.",
      },
    ],
    related: [
      {
        label: "Garage & Shed Clear-Outs",
        href: "/garage-shed-clear-outs",
        desc: "Clearing outdoor storage areas along with the yard.",
      },
      {
        label: "Property Clear-Outs",
        href: "/property-clear-outs",
        desc: "Larger clear-outs covering the whole property, inside and out.",
      },
    ],
    faqs: [
      {
        q: "What counts as green waste?",
        a: "Generally grass clippings, leaves, branches, prunings, hedge trimmings, plants and other organic garden material. Send a photo if you're unsure.",
      },
      {
        q: "Do you take soil, turf or rocks?",
        a: "These materials are much heavier than green waste and may be quoted differently. Let us know what's involved when requesting your quote.",
      },
      {
        q: "Do I need to bundle or bag everything first?",
        a: "No. A loose pile is fine where access allows. Bagged or bundled material can make loading faster, but it's not required.",
      },
      {
        q: "Can you take treated or painted timber?",
        a: "Treated, painted or manufactured timber isn't green waste and may require different disposal arrangements. Ask before booking and we'll confirm.",
      },
      {
        q: "How is green waste quoted?",
        a: "The same way as our other jobs, send photos and we'll assess the volume, then confirm an upfront price before we start.",
      },
    ],
  },
];

export const getWasteService = (slug: string) => wasteServices.find((s) => s.slug === slug);
